import { requireDefined } from '@nutrien/ddc-fe-util';
import { Auth } from 'aws-amplify';
import localforage from 'localforage';
import { v4 as uuid4 } from 'uuid';

import { addDocumentInfo } from '../../graphql';
import { isJestTest } from '../../test-helpers/isJestTest';
import useDebug from '../useDebug';
import { useOnlineStatus } from '../useOnlineStatus';
import {
  getLocalStorageSelectedDisciplineId,
  getLocalStorageSelectedSiteId,
  getRequestUrlByCollectionName,
} from '../utilityFunctions';

export const trackingEventStore = localforage.createInstance({
  name: 'tracking-event-store',
});

export enum TrackingEventType {
  AUDIT_LOG_FAILED = 'AUDIT_LOG_FAILED',
  SERVICE_FAILED = 'SERVICE_FAILED',
  NOTIFICATION_BULK_WRITE = 'NOTIFICATION_BULK_WRITE',
  AIMS_HEART_BEAT = 'AIMS_HEART_BEAT',
  DATA_LOG = 'DATA_LOG',
}

// get user details
export const getUserDetails = async () => {
  let userInfo = undefined;
  try {
    const loggedInuser = await Auth.currentAuthenticatedUser({ bypassCache: true });
    userInfo = await loggedInuser.getSignInUserSession();
  } catch (err) {
    console.log('🚀 ~ file: useEventTracking.ts ~ line 40 ~ err', err);
  }

  const userDetails = {
    email: '',
  };

  if (userInfo) {
    // Adding this because of errors thrown
    userDetails.email = userInfo?.idToken?.payload?.Email || 'NO_EMAIL_FOUND';
  }

  return userDetails;
};

// Sends An Event to the server
const dispatchEvent = async (
  eventType: string,
  requestUrl: string,
  fkSite: number,
  fkDiscipline: number,
  documentData: string,
  appVersion: string,
  userAgent: string,
  response: string,
) => {
  await addDocumentInfo({
    eventType,
    requestUrl,
    fkSite,
    fkDiscipline,
    documentData,
    appVersion,
    userAgent,
    response,
  });
};

// Cache Event
const cacheEvent = async (
  id: string,
  eventType: string,
  requestUrl: string,
  fkSite: number,
  fkDiscipline: number,
  documentData: string,
  appVersion: string,
  userAgent: string,
  eventTimeOnDevice: string,
  response: string,
) =>
  trackingEventStore.setItem(id, {
    eventType,
    requestUrl,
    fkSite,
    fkDiscipline,
    documentData,
    appVersion,
    userAgent,
    eventTimeOnDevice,
    response,
  });

// Track Event
export const trackEventFn = async (
  type: string,
  blob: Record<string, unknown>,
  isOnline = true,
  DEBUG = false,
) => {
  // Create event data
  const eventTimeOnDevice = new Date().toISOString();
  const siteId = getLocalStorageSelectedSiteId();
  const disciplineId = getLocalStorageSelectedDisciplineId();
  const userDetails = await getUserDetails();
  const requestUrl =
    blob && blob.collection ? getRequestUrlByCollectionName(blob?.collection) : ' ';
  const response =
    blob && blob?.errMessage && JSON.parse(blob?.errMessage)?.innerErrors
      ? JSON.stringify(JSON.parse(blob?.errMessage)?.innerErrors)
      : ' ';
  const extraBlobData = {
    ...blob,
    email: userDetails.email,
    id: uuid4(),
    unixTimestamp: Date.now(),
  };
  const blobString = JSON.stringify(extraBlobData);

  // If online dispatch to server
  if (isOnline) {
    if (DEBUG) {
      console.log('[useEventTracking] Dispatching event to server');
      console.dir([type, blobString, eventTimeOnDevice, siteId, disciplineId]);
    }
    try {
      const result = await dispatchEvent(
        type,
        requestUrl,
        Number(siteId),
        Number(disciplineId),
        blobString,
        requireDefined(import.meta.env.VITE_APP_VERSION) || '',
        navigator.userAgent,
        response,
      );
      if (DEBUG) {
        console.log('[useEventTracking] Event dispatched to server');
        console.dir(result);
      }
    } catch (error) {
      if (DEBUG) {
        console.log('🚀 ~ file: useEventTracking.ts ~ line 27 ~ trackEvent ~ error', error);
        console.log('[useEventTracking] Error sending event storing event locally');
      }
      await cacheEvent(
        extraBlobData.id,
        type,
        requestUrl,
        Number(siteId),
        Number(disciplineId),
        blobString,
        requireDefined(import.meta.env.VITE_APP_VERSION) || '',
        navigator.userAgent,
        eventTimeOnDevice,
        response,
      );
    }
  }
  // else store in local storage
  else {
    if (DEBUG) console.log('[useEventTracking] Storing event locally');
    await cacheEvent(
      extraBlobData.id,
      type,
      requestUrl,
      Number(siteId),
      Number(disciplineId),
      blobString,
      requireDefined(import.meta.env.VITE_APP_VERSION) || '',
      navigator.userAgent,
      eventTimeOnDevice,
      response,
    );
  }
};

export const useEventTracking = () => {
  const isOnline = useOnlineStatus();
  const DEBUG = useDebug();

  // Send cached events
  const sendCachedEvents = async () => {
    if (DEBUG) console.log('[useEventTracking] Sending cached events');
    const cachedEvents = await trackingEventStore.keys();
    if (DEBUG) {
      console.log('[useEventTracking] Cached events');
      console.dir(cachedEvents);
    }
    cachedEvents.forEach(async (key: string) => {
      try {
        const {
          eventType,
          appVersion,
          documentData,
          fkSite,
          fkDiscipline,
          requestUrl,
          userAgent,
          response,
        } = await trackingEventStore.getItem(key);
        if (DEBUG)
          console.log(
            '[useEventTracking] Event data',
            eventType,
            requestUrl,
            Number(fkSite),
            Number(fkDiscipline),
            documentData,
            appVersion,
            userAgent,
          );
        await dispatchEvent(
          eventType,
          requestUrl,
          Number(fkSite),
          Number(fkDiscipline),
          documentData,
          appVersion,
          userAgent,
          response,
        );
        await trackingEventStore.removeItem(key);
      } catch (error) {
        if (DEBUG) {
          console.log('🚀 ~ file: useEventTracking.ts ~ line 67 ~ sendCachedEvents ~ error', error);
          console.log('[useEventTracking] Error sending cached event', key);
        }
      }
    });

    if (DEBUG) console.log('[useEventTracking] Cached events sent');
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const trackEvent = async (type: TrackingEventType, blob: Record<string, any>) => {
    if (isJestTest()) return;
    await trackEventFn(type, blob, isOnline, DEBUG);
  };

  return { trackEvent, sendCachedEvents };
};

export default useEventTracking;
